// GENERATED by scripts/sync-blockgate-deno.mjs from src/ — DO NOT EDIT.
// Edit the src/ originals, then run `npm run sync:blockgate`.

// Safe TSL Block validator. Rejects anything outside the whitelisted grammar and enforces hard resource
// budgets BEFORE a block can be published. Pure + deterministic; runs server-side in-edge (no isolate
// needed — a bounded AST cannot run arbitrary code). Loops/recursion/dynamic access/codegen/side-effects/
// nondeterminism are simply not representable; this layer additionally checks types, references, and
// numeric budgets, and applies category-specific rules.

import {
  BINOPS, CMPOPS, INDICATORS, RISK_CTX, SIZING_CTX, SOURCES, TSL_DSL_VERSION, UNOPS,
  walk,
  type Expr, type TslBlock,
} from './ast.ts';
import { checkTslBlockShape } from './astGuard.ts';
import type { ParameterSchema } from './customBlockLibrary.ts';

export const TSL_LIMITS = {
  maxNodes: 300,        // total AST nodes across the block (= max ops/bar, since there are no loops)
  maxLookback: 500,     // max indicator period / prev barsAgo
  maxState: 8,          // bounded scalar state vars
  maxParams: 16,
  maxIndicatorsPerBlock: 32,
};

export interface TslValidation { ok: boolean; errors: string[]; lookback: number; nodes: number; }

type Ty = 'num' | 'bool';
const isInt = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n);

/** Shared by AST admission and the explicit parameter-schema text form. Unbounded
 * numeric params may be public, but runTslGate never marks them optimizer-safe. */
export function validateTslParamSpec(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'parameter spec must be an object';
  const s = value as Record<string, unknown>;
  const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  if (s.optimizable !== undefined && typeof s.optimizable !== 'boolean') return 'optimizable must be boolean';
  if (s.label !== undefined && typeof s.label !== 'string') return 'label must be a string';
  if (s.kind === 'int' || s.kind === 'float') {
    if (!finite(s.default) || (s.kind === 'int' && !Number.isInteger(s.default))) return 'numeric default must be finite (integer for int)';
    for (const k of ['min', 'max', 'step']) if (s[k] !== undefined && (!finite(s[k]) || (s.kind === 'int' && !Number.isInteger(s[k])))) return `${k} must be finite (integer for int)`;
    if (finite(s.min) && (s.default < s.min || (finite(s.max) && s.min > s.max))) return 'numeric bounds must be ordered and contain the default';
    if (finite(s.max) && s.default > s.max) return 'numeric bounds must contain the default';
    if (finite(s.step) && s.step <= 0) return 'step must be positive';
  } else if (s.kind === 'bool') {
    if (typeof s.default !== 'boolean') return 'boolean default must be true or false';
  } else if (s.kind === 'enum') {
    if (!Array.isArray(s.values) || !s.values.length || !s.values.every(v => typeof v === 'string' || finite(v)) || !s.values.some(v => v === s.default)) return 'enum default must belong to its finite number/string values';
  } else return 'unknown parameter kind';
  return null;
}

function integerDomain(e: Expr | undefined, schema: ParameterSchema, min: number, max = Number.MAX_SAFE_INTEGER): number | null {
  if (e?.t === 'num') return Number.isSafeInteger(e.v) && e.v >= min && e.v <= max ? e.v : null;
  if (e?.t === 'param') {
    const s = Object.prototype.hasOwnProperty.call(schema, e.name) ? schema[e.name] : undefined;
    if (s?.kind === 'int' && Number.isSafeInteger(s.min) && Number.isSafeInteger(s.max)
      && s.min! >= min && s.max! >= s.min! && s.max! <= max
      && typeof s.default === 'number' && Number.isSafeInteger(s.default) && s.default >= s.min! && s.default <= s.max!) return s.max!;
  }
  return null; // the entire optimizer domain must be legal, not just the default
}

export function validateTslBlock(b: TslBlock): TslValidation {
  const errors: string[] = [];
  const push = (m: string) => errors.push(m);
  if (b?.dsl !== TSL_DSL_VERSION) return { ok: false, errors: [`unsupported dsl '${(b as any)?.dsl}'`], lookback: 0, nodes: 0 };
  // ITERATIVE structural pre-pass (depth/unique-node/cycle/shape/reserved-key guard): the recursive type
  // checker below must never be reached by an AST that could blow the JS stack (10k-deep nesting), loop
  // forever (cyclic tslAst from a tampered share), or smuggle __proto__/constructor identifier names.
  const structErr = checkTslBlockShape(b);
  if (structErr) return { ok: false, errors: [`malformed TSL block: ${structErr}`], lookback: 0, nodes: 0 };
  const schema: ParameterSchema = b.params ?? {};
  const ctxFields = b.category === 'sizing' ? new Set(SIZING_CTX) : b.category === 'risk_rules' ? new Set(RISK_CTX) : new Set<string>();
  const stateNames = new Set(Object.keys(b.state ?? {}));

  const categories: Record<string, string> = { indicator: 'indicators', pattern: 'indicators', analysis: 'indicators', signal_generator: 'indicators', filter: 'filters', qty_calculator: 'sizing', sl_calculator: 'sizing', tp_calculator: 'sizing', risk_rule: 'risk_rules' };
  if (!Object.prototype.hasOwnProperty.call(categories, b.blockType) || categories[b.blockType] !== b.category) push('blockType and category must agree');
  const reservedParams = new Set<string>([...SOURCES, ...ctxFields, 'true', 'false', 'and', 'or', 'not']);
  for (const [name, spec] of Object.entries(schema)) {
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name) || reservedParams.has(name)) push(`reserved or invalid param name '${name}'`);
    const error = validateTslParamSpec(spec); if (error) push(`param '${name}': ${error}`);
  }
  for (const name of stateNames) if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) push(`invalid state name '${name}'`);

  if (Object.keys(schema).length > TSL_LIMITS.maxParams) push(`too many params (> ${TSL_LIMITS.maxParams})`);
  if (stateNames.size > TSL_LIMITS.maxState) push(`too many state vars (> ${TSL_LIMITS.maxState})`);

  let nodes = 0, lookback = 0, indicatorCount = 0;

  // Match the interpreter's per-node memoization. Visual ASTs may share subtrees;
  // revisiting each incoming edge both overcounts the budget and takes exponential time.
  const types = new WeakMap<Expr, Ty>();
  const ty = (e: Expr): Ty => {
    const cached = types.get(e); if (cached) return cached;
    const result = infer(e); types.set(e, result); return result;
  };
  // type inference + per-node legality
  const infer = (e: Expr): Ty => {
    nodes++;
    // sizing blocks are pure functions of ctx+params (no series/streaming nodes).
    if (b.category === 'sizing' && (e.t === 'prev' || e.t === 'cross' || e.t === 'state' || e.t === 'indicator' || e.t === 'src')) push(`sizing blocks may not use '${e.t}' (qty must be a pure function of ctx + params)`);
    switch (e.t) {
      case 'num': if (!Number.isFinite(e.v)) push('non-finite numeric literal'); return 'num';
      case 'bool': return 'bool';
      case 'param': {
        const s = Object.prototype.hasOwnProperty.call(schema, e.name) ? schema[e.name] : undefined;
        if (!s) { push(`unknown param '${e.name}'`); return 'num'; }
        if (s.kind === 'enum' && (!Array.isArray(s.values) || !s.values.every(v => typeof v === 'number' && Number.isFinite(v)))) push(`enum param '${e.name}' needs numeric values when used in an expression`);
        return s.kind === 'bool' ? 'bool' : 'num';
      }
      case 'src': if (!SOURCES.includes(e.src as any)) push(`unknown source '${e.src}'`); if (b.category === 'sizing' || b.category === 'risk_rules') { if (b.category === 'sizing') push('sizing blocks may not read OHLCV series'); } return 'num';
      case 'ctx': if (!ctxFields.has(e.field)) push(`ctx field '${e.field}' not available for category '${b.category}'`); return 'num';
      case 'state': if (!stateNames.has(e.name)) push(`unknown state var '${e.name}'`); return 'num';
      case 'indicator': {
        indicatorCount++;
        const spec = Object.prototype.hasOwnProperty.call(INDICATORS, e.name) ? INDICATORS[e.name] : undefined;
        if (!spec) { push(`unknown indicator '${e.name}'`); return 'num'; }
        if (e.args.length !== spec.arity) push(`indicator '${e.name}' expects ${spec.arity} args, got ${e.args.length}`);
        if (b.category === 'sizing') push('sizing blocks may not use series indicators');
        e.args.forEach((a, i) => { if (ty(a) !== 'num') push(`indicator '${e.name}' arg ${i} must be numeric`); });
        // ATR's period is arg 0; other windowed indicators use arg 1. Missing
        // arguments still produce diagnostics instead of throwing inside validation.
        if (e.name === 'atr' || spec.arity === 2 || e.name === 'htf') {
          const pb = integerDomain(e.args[e.name === 'atr' ? 0 : 1], schema, 1);
          if (pb === null) push(`indicator '${e.name}' period must be a positive integer literal or bounded int param with an in-range default`);
          else lookback = Math.max(lookback, pb);
        }
        if (e.name === 'htf' || e.name === 'htfema1') {
          if (integerDomain(e.args[0], schema, 1, Math.floor(Number.MAX_SAFE_INTEGER / 60000)) === null) push(`indicator '${e.name}' minutes must be a positive bounded integer`);
          if (e.name === 'htf' && integerDomain(e.args[2], schema, 0, 3) === null) push("indicator 'htf' kind must be an integer in 0..3");
        }
        return 'num';
      }
      case 'prev': {
        if (!isInt(e.barsAgo) || e.barsAgo < 0) push('prev barsAgo must be a non-negative integer');
        else lookback = Math.max(lookback, e.barsAgo);
        return ty(e.expr);
      }
      case 'unop': { if (!UNOPS.includes(e.op)) push(`bad unop '${e.op}'`); const a = ty(e.a); if (e.op === 'not') { if (a !== 'bool') push('not expects boolean'); return 'bool'; } if (a !== 'num') push(`${e.op} expects number`); return 'num'; }
      case 'binop': { if (!BINOPS.includes(e.op)) push(`bad binop '${e.op}'`); const a = ty(e.a), c = ty(e.b); if (a !== 'num' || c !== 'num') push(`${e.op} expects numbers`); return 'num'; }
      case 'cmp': { if (!CMPOPS.includes(e.op)) push(`bad cmp '${e.op}'`); const a = ty(e.a), c = ty(e.b); if (a !== 'num' || c !== 'num') push(`${e.op} expects numbers`); return 'bool'; }
      case 'logic': { if (e.op !== 'and' && e.op !== 'or') push(`bad logic '${e.op}'`); const a = ty(e.a), c = ty(e.b); if (a !== 'bool' || c !== 'bool') push(`${e.op} expects booleans`); return 'bool'; }
      case 'cross': { if (e.dir !== 'over' && e.dir !== 'under') push(`bad cross dir`); const a = ty(e.a), c = ty(e.b); if (a !== 'num' || c !== 'num') push('cross expects numbers'); return 'bool'; }
      case 'cond': { if (ty(e.cond) !== 'bool') push('cond condition must be boolean'); const t = ty(e.then), f = ty(e.else); if (t !== f) push('cond branches must have the same type'); return t; }
      default: push(`unknown node '${(e as any).t}'`); return 'num';
    }
  };

  const expect = (e: Expr | undefined, want: Ty, label: string) => { if (!e) { push(`${label} required`); return; } const got = ty(e); if (got !== want) push(`${label} must be ${want}, got ${got}`); };

  // category-specific body rules
  switch (b.category) {
    case 'indicators': case 'filters': {
      if (!b.value && !b.signal) push('indicator/filter needs a value and/or signal expression');
      if (b.value) expect(b.value, 'num', 'value');
      if (b.signal) expect(b.signal, 'bool', 'signal');
      if (b.qty || b.risk) push('indicator/filter must not define qty/risk');
      break;
    }
    case 'sizing': {
      expect(b.qty, 'num', 'qty');
      if (b.value || b.signal || b.risk) push('sizing block must define only qty');
      break;
    }
    case 'risk_rules': {
      if (!b.risk) push('risk_rule needs a risk.action expression'); else expect(b.risk.action, 'num', 'risk.action');
      if (b.value || b.signal || b.qty) push('risk_rule must define only risk.action');
      break;
    }
    default: push(`unknown category '${b.category}'`);
  }
  // state update expressions must be numeric
  for (const [name, s] of Object.entries(b.state ?? {})) { if (!Number.isFinite(s.init)) push(`state '${name}' init must be finite`); expect(s.update, 'num', `state '${name}' update`); }

  if (nodes > TSL_LIMITS.maxNodes) push(`AST too large (${nodes} > ${TSL_LIMITS.maxNodes} nodes)`);
  if (indicatorCount > TSL_LIMITS.maxIndicatorsPerBlock) push(`too many indicators (${indicatorCount})`);
  if (lookback > TSL_LIMITS.maxLookback) push(`lookback ${lookback} exceeds ${TSL_LIMITS.maxLookback}`);

  return { ok: errors.length === 0, errors, lookback, nodes };
}

/** Defensive guard: a value claiming to be a TSL block must not smuggle host code. */
export function hasForbiddenCodeFields(o: unknown): boolean {
  if (!o || typeof o !== 'object') return false;
  for (const k of ['code', 'eval', 'fn', 'js', 'source']) if (k in (o as Record<string, unknown>)) return true;
  let found = false;
  try { walk((o as TslBlock).value, (n) => { if ((n as any).t === undefined) found = true; }); } catch { /* */ }
  return found;
}
